import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import {
  RecipeMakingTip,
  RecipeMakingTipResponse,
  RecipeMakingTipsResponse,
  RecipeMakingTipStats
} from '@/src/types/apps/recipeMakingTips'
import { RecipeCountry } from '@/src/types/apps/recipeGuideTypes'

export function extractTipFromApiResponse(payload: unknown): RecipeMakingTip | null {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const layer1 = (payload as { data?: RecipeMakingTip }).data ?? (payload as RecipeMakingTip)
  const layer2 =
    layer1 && typeof layer1 === 'object' && 'data' in layer1
      ? ((layer1 as { data?: RecipeMakingTip }).data ?? (layer1 as RecipeMakingTip))
      : (layer1 as RecipeMakingTip)

  return layer2?.id != null ? layer2 : null
}

interface RecipeMakingTipsState {
  tips: RecipeMakingTip[]
  tip: RecipeMakingTip | null
  stats: RecipeMakingTipStats
  loading: boolean
  meta: RecipeMakingTipsResponse['meta']
  error: unknown
  success: string | null
}

const initialState: RecipeMakingTipsState = {
  tips: [],
  tip: null,
  stats: {
    total: 0,
    published: 0,
    draft: 0
  },
  loading: false,
  meta: {
    total: 0,
    pages: 0
  },
  error: null,
  success: null
}

type QueryOptions = {
  take?: number
  skip?: number
  sortBy?: string
  filters?: Record<string, string>
}

export const getRecipeMakingTips = createAsyncThunk<
  RecipeMakingTipsResponse,
  QueryOptions,
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygRecipeMakingTips/getRecipeMakingTips', async (params, { rejectWithValue, extra }) => {
  try {
    const response = await (extra.httpClient as { get: Function }).get('recipe-making-tips', { params })

    return response.data
  } catch (error: unknown) {
    return rejectWithValue((error as { response?: { data?: unknown } })?.response?.data ?? error)
  }
})

export const getRecipeMakingTip = createAsyncThunk<
  RecipeMakingTipResponse,
  number,
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygRecipeMakingTips/getRecipeMakingTip', async (id, { rejectWithValue, extra }) => {
  try {
    const response = await (extra.httpClient as { get: Function }).get(`recipe-making-tips/${id}`)

    return response.data
  } catch (error: unknown) {
    return rejectWithValue((error as { response?: { data?: unknown } })?.response?.data ?? error)
  }
})

export const createRecipeMakingTip = createAsyncThunk<
  RecipeMakingTipResponse,
  Partial<RecipeMakingTip>,
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygRecipeMakingTips/createRecipeMakingTip', async (data, { rejectWithValue, extra }) => {
  try {
    const response = await (extra.httpClient as { post: Function }).post('recipe-making-tips', data)

    return response.data
  } catch (error: unknown) {
    return rejectWithValue((error as { response?: { data?: unknown } })?.response?.data ?? error)
  }
})

export const updateRecipeMakingTip = createAsyncThunk<
  RecipeMakingTipResponse,
  { id: number; data: Partial<RecipeMakingTip> },
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygRecipeMakingTips/updateRecipeMakingTip', async ({ id, data }, { rejectWithValue, extra }) => {
  try {
    const response = await (extra.httpClient as { put: Function }).put(`recipe-making-tips/${id}`, data)

    return response.data
  } catch (error: unknown) {
    return rejectWithValue((error as { response?: { data?: unknown } })?.response?.data ?? error)
  }
})

export const deleteRecipeMakingTip = createAsyncThunk<
  { id: number },
  number,
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygRecipeMakingTips/deleteRecipeMakingTip', async (id, { rejectWithValue, extra }) => {
  try {
    await (extra.httpClient as { delete: Function }).delete(`recipe-making-tips/${id}`)

    return { id }
  } catch (error: unknown) {
    return rejectWithValue((error as { response?: { data?: unknown } })?.response?.data ?? error)
  }
})

export const getRecipeMakingTipStats = createAsyncThunk<
  { data: RecipeMakingTipStats },
  { country?: RecipeCountry },
  { rejectValue: unknown; extra: { httpClient: unknown } }
>('gygRecipeMakingTips/getRecipeMakingTipStats', async ({ country }, { rejectWithValue, extra }) => {
  try {
    const response = await (extra.httpClient as { get: Function }).get('recipe-making-tips/stats', {
      params: { country }
    })

    return response.data
  } catch (error: unknown) {
    return rejectWithValue((error as { response?: { data?: unknown } })?.response?.data ?? error)
  }
})

export const uploadRecipeMakingTipFile = createAsyncThunk<
  RecipeMakingTipResponse,
  {
    id: number
    file: File
    mediaField: 'tip' | 'procedure'
    procedureId?: number
    procedureUniqueId?: string
  },
  { rejectValue: unknown; extra: { httpClient: unknown } }
>(
  'gygRecipeMakingTips/uploadRecipeMakingTipFile',
  async ({ id, file, mediaField, procedureId, procedureUniqueId }, { rejectWithValue, extra }) => {
    try {
      const formData = new FormData()
      formData.append('mediaField', mediaField)
      if (procedureId) {
        formData.append('procedureId', procedureId.toString())
      }
      if (procedureUniqueId) {
        formData.append('procedureUniqueId', procedureUniqueId)
      }
      formData.append('file', file)

      const response = await (extra.httpClient as { post: Function }).post(`recipe-making-tips/${id}/media`, formData, {
        timeout: 120_000
      })

      return response.data
    } catch (error: unknown) {
      return rejectWithValue((error as { response?: { data?: unknown } })?.response?.data ?? error)
    }
  }
)

const gygRecipeMakingTipsSlice = createSlice({
  name: 'gygRecipeMakingTips',
  initialState,
  reducers: {
    clearRecipeMakingTipSuccess(state) {
      state.success = null
    },
    clearRecipeMakingTipError(state) {
      state.error = null
    }
  },
  extraReducers: builder => {
    builder.addCase(getRecipeMakingTips.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(getRecipeMakingTips.fulfilled, (state, action) => {
      state.loading = false
      state.tips = action.payload.data
      state.meta = action.payload.meta
    })
    builder.addCase(getRecipeMakingTips.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })

    builder.addCase(getRecipeMakingTip.pending, state => {
      state.loading = true
      state.error = null
      state.tip = null
    })
    builder.addCase(getRecipeMakingTip.fulfilled, (state, action) => {
      state.loading = false
      state.tip = extractTipFromApiResponse(action.payload)
    })
    builder.addCase(getRecipeMakingTip.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })

    builder.addCase(createRecipeMakingTip.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(createRecipeMakingTip.fulfilled, state => {
      state.loading = false
      state.success = 'Recipe making tip created successfully'
    })
    builder.addCase(createRecipeMakingTip.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })

    builder.addCase(updateRecipeMakingTip.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(updateRecipeMakingTip.fulfilled, (state, action) => {
      state.loading = false
      state.tip = extractTipFromApiResponse(action.payload) ?? state.tip
      state.success = 'Recipe making tip updated successfully'
    })
    builder.addCase(updateRecipeMakingTip.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })

    builder.addCase(deleteRecipeMakingTip.pending, state => {
      state.loading = true
      state.error = null
    })
    builder.addCase(deleteRecipeMakingTip.fulfilled, (state, action) => {
      state.loading = false
      state.tips = state.tips.filter(tip => tip.id !== action.payload.id)
      state.meta.total = Math.max(0, state.meta.total - 1)
      state.success = 'Recipe making tip deleted successfully'
    })
    builder.addCase(deleteRecipeMakingTip.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload
    })

    builder.addCase(getRecipeMakingTipStats.fulfilled, (state, action) => {
      state.stats = action.payload.data
    })

    builder.addCase(uploadRecipeMakingTipFile.fulfilled, (state, action) => {
      state.tip = extractTipFromApiResponse(action.payload) ?? state.tip
    })
  }
})

export const { clearRecipeMakingTipSuccess, clearRecipeMakingTipError } = gygRecipeMakingTipsSlice.actions
export default gygRecipeMakingTipsSlice.reducer
